import { Matrix4, Quaternion, Vector3 } from 'three'

/**
 * Flat oriented boxes and ray tests on the XZ plane.
 *
 * The original does this with Unity's 3D physics — `Physics.RaycastAll` against
 * BoxColliders. Everything that matters here is flat: buses sit on the ground,
 * never bank, and the rays run horizontally. So a 2D OBB test on XZ gives the
 * same answers as the 3D one, without a physics engine.
 */

export interface Obb2D {
  /** Centre on the XZ plane. */
  cx: number
  cz: number
  /** Half extents along the box's own right/forward axes. */
  halfWidth: number
  halfLength: number
  /** Unit forward vector on XZ. */
  fx: number
  fz: number
}

const _v = new Vector3()

/**
 * Build a flat OBB for a bus.
 *
 * `position`/`rotation` are already in three.js space. Local forward for a bus
 * is −Z here (Unity's +Z through the mirror), and `centreOffset` shifts the box
 * along that forward, matching `BoxCollider.m_Center.z`.
 */
export function busObb(
  position: Vector3,
  rotation: Quaternion,
  width: number,
  length: number,
  centreOffset = 0,
): Obb2D {
  _v.set(0, 0, -1).applyQuaternion(rotation)
  const fx = _v.x
  const fz = _v.z
  const inv = Math.hypot(fx, fz) || 1
  const nfx = fx / inv
  const nfz = fz / inv
  return {
    cx: position.x + nfx * centreOffset,
    cz: position.z + nfz * centreOffset,
    halfWidth: width / 2,
    halfLength: length / 2,
    fx: nfx,
    fz: nfz,
  }
}

/**
 * Ray vs flat OBB. Returns the distance along the ray to the near face, or
 * `null` if it misses.
 *
 * `insideCounts: false` reproduces Unity's behaviour of not reporting a
 * collider the ray starts inside — which the original relies on so a bus's own
 * raycast points, which sit inside its collider, do not hit it.
 */
export function raycastObb(
  originX: number,
  originZ: number,
  dirX: number,
  dirZ: number,
  box: Obb2D,
  maxDistance = Infinity,
  insideCounts = false,
): number | null {
  // Work in the box's local frame: forward = (fx, fz), right = (fz, -fx).
  const rx = box.fz
  const rz = -box.fx

  const dx = originX - box.cx
  const dz = originZ - box.cz

  const localX = dx * rx + dz * rz
  const localZ = dx * box.fx + dz * box.fz
  const localDirX = dirX * rx + dirZ * rz
  const localDirZ = dirX * box.fx + dirZ * box.fz

  if (
    !insideCounts &&
    Math.abs(localX) <= box.halfWidth &&
    Math.abs(localZ) <= box.halfLength
  ) {
    return null
  }

  let tMin = -Infinity
  let tMax = Infinity

  for (const [o, d, half] of [
    [localX, localDirX, box.halfWidth],
    [localZ, localDirZ, box.halfLength],
  ] as const) {
    if (Math.abs(d) < 1e-9) {
      if (o < -half || o > half) return null
      continue
    }
    const t1 = (-half - o) / d
    const t2 = (half - o) / d
    tMin = Math.max(tMin, Math.min(t1, t2))
    tMax = Math.min(tMax, Math.max(t1, t2))
    if (tMin > tMax) return null
  }

  if (tMax < 0) return null
  const t = tMin >= 0 ? tMin : tMax
  if (t < 0 || t > maxDistance) return null
  return t
}

/**
 * Rotation that faces a bus along `forward`.
 *
 * Built from an explicit basis rather than `setFromUnitVectors`, which picks
 * the shortest arc between two vectors and can roll the bus about its own axis
 * on a curve. Columns are (right, up, back), where back is -forward because
 * three.js objects face -Z.
 */
const _up = new Vector3(0, 1, 0)
const _right = new Vector3()
const _back = new Vector3()
const _basis = new Matrix4()
const _quat = new Quaternion()

export function orientAlong(forward: Vector3): Quaternion {
  _right.crossVectors(forward, _up).normalize()
  if (_right.lengthSq() < 1e-9) _right.set(1, 0, 0)
  _back.copy(forward).negate()
  _basis.makeBasis(_right, _up, _back)
  return _quat.setFromRotationMatrix(_basis)
}

/** Do two flat OBBs overlap? Separating-axis test over the four face normals. */
export function obbOverlap(a: Obb2D, b: Obb2D): boolean {
  const axes: [number, number][] = [
    [a.fx, a.fz],
    [a.fz, -a.fx],
    [b.fx, b.fz],
    [b.fz, -b.fx],
  ]
  const dx = b.cx - a.cx
  const dz = b.cz - a.cz
  for (const [ax, az] of axes) {
    const centreGap = Math.abs(dx * ax + dz * az)
    const reachA =
      Math.abs(a.fx * ax + a.fz * az) * a.halfLength +
      Math.abs(a.fz * ax - a.fx * az) * a.halfWidth
    const reachB =
      Math.abs(b.fx * ax + b.fz * az) * b.halfLength +
      Math.abs(b.fz * ax - b.fx * az) * b.halfWidth
    if (centreGap > reachA + reachB) return false
  }
  return true
}

/** Nearest point on the OBB's boundary to `(px, pz)` — Unity's `Collider.ClosestPoint`. */
export function closestPointOnObb(px: number, pz: number, box: Obb2D): { x: number; z: number } {
  const rx = box.fz
  const rz = -box.fx
  const dx = px - box.cx
  const dz = pz - box.cz
  const localX = Math.max(-box.halfWidth, Math.min(box.halfWidth, dx * rx + dz * rz))
  const localZ = Math.max(-box.halfLength, Math.min(box.halfLength, dx * box.fx + dz * box.fz))
  return {
    x: box.cx + rx * localX + box.fx * localZ,
    z: box.cz + rz * localX + box.fz * localZ,
  }
}
